/** Cognito JWT issuer claim helpers for realm / pool matching. */

export function buildCognitoIssuer(userPoolId: string): string {
  const region = userPoolId.split("_")[0];
  if (!region) {
    throw new Error("Invalid Cognito user pool id.");
  }
  return `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`;
}

function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  const parts = jwt.split(".");
  if (parts.length !== 3) {
    return null;
  }
  try {
    const padded = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const full = padded + "=".repeat((4 - (padded.length % 4)) % 4);
    const json = atob(full);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Returns the JWT `iss` claim, or null when missing or malformed. */
export function getJwtIssuer(jwt: string): string | null {
  const payload = decodeJwtPayload(jwt);
  const issuer = payload?.iss;
  return typeof issuer === "string" && issuer.trim() ? issuer : null;
}

/** True when the JWT was issued by the given Cognito user pool. */
export function jwtMatchesUserPool(jwt: string, userPoolId: string): boolean {
  const issuer = getJwtIssuer(jwt);
  if (!issuer) {
    return false;
  }
  try {
    return issuer === buildCognitoIssuer(userPoolId);
  } catch {
    return false;
  }
}

export class AuthTokenRealmMismatchError extends Error {
  constructor(message = "ID token does not match this app's Cognito user pool.") {
    super(message);
    this.name = "AuthTokenRealmMismatchError";
  }
}
